import { useEffect } from "react";
import { type PlaceFeature, type PlaceGeoJSON, type RelatedPlace, getPlaceTrack } from "../lib/api";
import type { LandingPageViewData } from "../components/landing/types";
import { FIT_BOUNDS_PADDING, LANDING_PAGE_FLY_TO_ZOOM } from "../lib/mapConstants";
import { routes } from "../lib/routes";
import { useShare } from "../hooks/useShare";
import {
  Map,
  useViewport,
  usePanel,
  usePanelPosition,
  AdventureMapPlacesLayer,
  RelatedPlacesLayer,
  RouteTrackLayer,
  SelectedPlaceLayer,
  CompassControl,
  ZoomControls,
} from "../components/map";
import { toDetailLevel, type DetailLevel } from "~/lib/detailLevel";
import { Button } from "../components/ui/button";
import { Icon } from "../components/ui/icons/icon";
import { GroupedPlaceCards } from "../components/landing/place-cards";
import { PlaceDetailContent } from "../components/landing/PlaceDetailContent";
import { BlobDecoration } from "../components/BlobDecoration";

interface MobileLandingPageProps {
  landingPage: LandingPageViewData;
  places: PlaceGeoJSON;
  selectedPlace: PlaceFeature | null;
  onPlaceSelect: (placeId: string) => void;
  onPlaceDeselect: () => void;
  onSelectRelatedPlace?: (place: RelatedPlace) => void;
}

export default function MobileLandingPage({
  landingPage,
  places,
  selectedPlace,
  onPlaceSelect,
  onPlaceDeselect,
  onSelectRelatedPlace,
}: MobileLandingPageProps) {
  return (
    <Map.Canvas bounds={places} padding={FIT_BOUNDS_PADDING.mobile} className="nature-minimal">
      <MobileLandingContent
        landingPage={landingPage}
        places={places}
        selectedPlace={selectedPlace}
        onPlaceSelect={onPlaceSelect}
        onPlaceDeselect={onPlaceDeselect}
        onSelectRelatedPlace={onSelectRelatedPlace}
      />
    </Map.Canvas>
  );
}

/** Inner component that uses Map.* hooks */
function MobileLandingContent({
  landingPage,
  places,
  selectedPlace,
  onPlaceSelect,
  onPlaceDeselect,
  onSelectRelatedPlace,
}: MobileLandingPageProps) {
  const share = useShare();
  const { flyTo, isReady } = useViewport();
  const { position, snapTo } = usePanel();
  const details = toDetailLevel(position);

  usePanelPosition("preview", !!selectedPlace);

  const handleSelectRelatedPlace = (place: RelatedPlace) => {
    snapTo("preview");
    onSelectRelatedPlace?.(place);
  };

  // Fly to selected place when it changes (includes related places in viewport if any)
  useEffect(() => {
    if (!selectedPlace || !isReady) return;
    const center = selectedPlace.geometry.coordinates as [number, number];
    const fitWith =
      selectedPlace.properties.relatedPlaces?.map((p) => [p.longitude, p.latitude] as [number, number]) ?? [];
    flyTo(center, { zoom: LANDING_PAGE_FLY_TO_ZOOM, fitWith });
  }, [selectedPlace, isReady, flyTo]);

  const handlePlaceClick = (feature: PlaceFeature) => {
    onPlaceSelect(feature.id as string);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    if (landingPage.slug) {
      share({
        url: routes.to.landingPages.bySlug(landingPage.slug),
        title: landingPage.name,
      });
    }
  };

  const emptyState = (
    <div className="px-4 py-8 text-center text-muted-foreground">
      <p className="text-sm">No places on this map yet</p>
    </div>
  );

  return (
    <>
      {/* Map layers (order = z-index: first = bottom) */}
      <RouteTrackLayer track={selectedPlace ? getPlaceTrack(selectedPlace.properties) : undefined} />
      <AdventureMapPlacesLayer
        places={places}
        excludeIds={selectedPlace?.properties.relatedPlaces?.map((p) => p.id)}
        onClick={handlePlaceClick}
      />
      <RelatedPlacesLayer
        relatedPlaces={selectedPlace?.properties.relatedPlaces ?? []}
        onClick={handleSelectRelatedPlace}
      />
      <SelectedPlaceLayer place={selectedPlace} />

      {/* Map controls */}
      <Map.Controls>
        <CompassControl />
        <ZoomControls />
      </Map.Controls>

      {landingPage.logo && (
        <Map.HeaderLeft>
          <img
            src={landingPage.logo.original}
            alt={`Brand logo for ${landingPage.name}`}
            className="h-10 w-auto object-contain"
          />
        </Map.HeaderLeft>
      )}

      {/* Bottom sheet panel */}
      <Map.Panel>
        {selectedPlace ? (
          <PlaceDetailContent
            key={selectedPlace.id}
            place={selectedPlace}
            details={details}
            onBack={onPlaceDeselect}
            onSelectRelatedPlace={handleSelectRelatedPlace}
          />
        ) : (
          <MapOverviewContent
            landingPage={landingPage}
            places={places}
            details={details}
            onShare={handleShare}
            emptyState={emptyState}
          />
        )}
      </Map.Panel>
    </>
  );
}

interface MapOverviewContentProps {
  landingPage: LandingPageViewData;
  places: PlaceGeoJSON;
  details: DetailLevel;
  onShare: (e: React.MouseEvent) => void;
  emptyState: React.ReactNode;
}

function MapOverviewContent({ landingPage, places, onShare, emptyState }: MapOverviewContentProps) {
  return (
    <div className="flex flex-col">
      {/* Header section */}
      <div className="px-4 pt-2 pb-4 border-b border-border">
        <h1 className="text-xl font-medium">{landingPage.name}</h1>
        {landingPage.curatedBy && (
          <p className="text-xs text-muted-foreground mt-1">Curated by {landingPage.curatedBy.displayName}</p>
        )}
        {landingPage.description && <p className="text-sm text-muted-foreground mt-2">{landingPage.description}</p>}
        <div className="mt-3">
          <Button asChild variant="outline" className="w-full text-primary hover:bg-muted hover:text-primary">
            <a href={landingPage.slug ? routes.to.landingPages.bySlug(landingPage.slug) : "#"} onClick={onShare}>
              <Icon i="share" className="size-4 mr-2" />
              Share
            </a>
          </Button>
        </div>
      </div>

      {/* Places list */}
      <div className="relative overflow-hidden">
        <BlobDecoration className="absolute -top-10 -right-10 w-40 h-40 opacity-20 text-chart-1" variant={1} />
        <BlobDecoration className="absolute bottom-20 -left-16 w-48 h-48 opacity-15 text-chart-2" variant={2} />
        <GroupedPlaceCards
          places={places.features}
          selectedId={null}
          sectionConfigs={landingPage.config?.sections}
          emptyState={emptyState}
        />

        {/* Footer CTA */}
        <div className="px-4 py-8 text-center border-t border-border mt-4">
          <p className="text-xs text-muted-foreground mb-3">
            Curated with care by {landingPage.curatedBy?.displayName ?? "your local adventurers"}
          </p>
          {/* eslint-disable-next-line react/jsx-no-target-blank -- preserve referrer for analytics */}
          <a
            href="https://noma.to"
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors hover:bg-muted border border-input text-primary"
          >
            Create your own map
            <Icon i="external-link" className="size-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
